import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';

import {
  fetchContagens,
  fetchConversas,
  type Configuracao,
  type ConversaResumo,
  type Fila,
} from '@/api/secretario';
import { Badge } from '@/components/ui/badge';
import { BarraDaLista } from '@/components/ui/barra-da-lista';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { FiltroDaLista } from '@/components/ui/filtro-da-lista';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/skeleton';
import { BuscaInteligente } from '@/components/ui/smart-search';
import { Spinner } from '@/components/ui/spinner';
import { AbasDaLista } from '@/components/ui/abas-da-lista';
import { useSmartSearch } from '@/hooks/useSmartSearch';
import { classifyError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import type { SmartSearchFields } from '@/lib/search/smartSearch';

import { classesDoTom, fraseDoQuePrecisa, nomeExibido, quando, situacaoDe, telefoneCasa } from './atendimento/util';

const PAGE_SIZE = 50;
// A aba é o destino do aviso: atendimentos novos precisam aparecer sem recarregar.
const REFETCH_MS = 30_000;

const VAZIO: Record<Fila, { title: string; description: string }> = {
  aguardando: { title: 'Nada aguardando você', description: 'Escalonamentos e retornos pendentes aparecem aqui.' },
  preparacao: { title: 'Nenhum atendimento em preparação', description: 'Depois do retorno, o atendimento fica aqui até virar processo ou ser encerrado.' },
  andamento: { title: 'Nenhum atendimento em curso', description: 'O Secretário não está atendendo ninguém agora.' },
  todos: { title: 'Nenhum atendimento ainda', description: 'Quando os seus clientes falarem com o Secretário no WhatsApp, os atendimentos aparecem aqui.' },
};

/**
 * A busca inteligente dos atendimentos: o nome do contato e os dígitos do telefone
 * no mesmo texto — "Sandra 6666" soma os dois. O telefone que o cliente cola do
 * WhatsApp ("+55 11 96666-5555", com o 9 que o número guardado pode não ter) não
 * casa como texto; esse caminho é o `telefoneCasa`, só para pergunta com cara de
 * telefone.
 */
const ATENDIMENTO_SEARCH_FIELDS: SmartSearchFields<ConversaResumo> = {
  text: (c) => `${c.contato_nome ?? ''}\n${c.contato_telefone.replace(/\D/g, '')}`,
};

/** Só dígitos e a pontuação de telefone: "+55 (11) 96666-5555". */
const PARECE_TELEFONE = /^[\d\s+().-]*\d[\d\s+().-]*$/;

const SEM_CONVERSAS: ConversaResumo[] = [];

const ABAS: { value: Fila; label: string }[] = [
  { value: 'aguardando', label: 'Aguardando você' },
  { value: 'preparacao', label: 'Em preparação' },
  { value: 'andamento', label: 'Em andamento' },
  { value: 'todos', label: 'Todos' },
];

/**
 * O inbox do advogado: o que precisa dele primeiro (docs/PLANO_SECRETARIO_ATENDIMENTOS.md).
 *
 * "Aguardando você" reúne o escalado que ninguém assumiu e a triagem concluída
 * sem retorno — o que tem gente esperando ou prazo correndo. "Em preparação" é
 * a triagem com retorno feito e sem desfecho: outro ritmo, outra aba, senão a
 * urgente de hoje afunda no meio das iniciais em redação.
 */
/** "Quem cuida" sem filtro, e a fila dos que pararam antes da escolha do advogado. */
const TODOS_OS_ADVOGADOS = 'todos';
const SEM_ADVOGADO = 'sem-advogado';

export function AtendimentosList({ config }: { config?: Configuracao | null }) {
  const [fila, setFila] = useState<Fila | null>(null);
  const [busca, setBusca] = useState('');
  // No escritório, quem gere vê todos e filtra por quem cuida (docs/PLANO_SECRETARIO_ESCRITORIO.md §6);
  // o participante vê só os dele — o backend já recorta.
  const doEscritorio = config?.escopo === 'ESCRITORIO';
  const gere = config?.papel !== 'PARTICIPANTE';
  const [quemCuida, setQuemCuida] = useState(TODOS_OS_ADVOGADOS);
  const filtraQuem = doEscritorio && gere ? quemCuida : TODOS_OS_ADVOGADOS;
  const advogadoId = filtraQuem !== TODOS_OS_ADVOGADOS && filtraQuem !== SEM_ADVOGADO ? filtraQuem : undefined;
  const semAdvogado = filtraQuem === SEM_ADVOGADO;

  const contagens = useQuery({
    queryKey: queryKeys.secretario.contagens(advogadoId ?? ''),
    queryFn: () => fetchContagens(advogadoId),
    refetchInterval: REFETCH_MS,
  });
  // Sem escolha do advogado: abre em "Aguardando você" quando há algo lá; senão em "Todos".
  const filaAtiva: Fila = fila ?? ((contagens.data?.aguardando ?? 0) > 0 ? 'aguardando' : 'todos');

  const lista = useInfiniteQuery({
    queryKey: queryKeys.secretario.atendimentos(filaAtiva, filtraQuem),
    queryFn: ({ pageParam }) =>
      fetchConversas({
        fila: filaAtiva,
        offset: pageParam,
        limit: PAGE_SIZE,
        ...(advogadoId ? { advogado_id: advogadoId } : {}),
        ...(semAdvogado ? { sem_advogado: true } : {}),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.length < PAGE_SIZE ? undefined : allPages.length * PAGE_SIZE),
    refetchInterval: REFETCH_MS,
  });

  const carregadas = useMemo(() => lista.data?.pages.flat() ?? SEM_CONVERSAS, [lista.data]);
  const search = useSmartSearch(carregadas, busca, ATENDIMENTO_SEARCH_FIELDS);
  const linhas = useMemo(() => {
    if (!search.active) return carregadas;
    const pelaBusca = new Set(search.results);
    // "-5555" é exclusão, não telefone: senão juntaria os sem 5555 aos com 5555.
    const telefone = PARECE_TELEFONE.test(busca.trim()) && !busca.trim().startsWith('-');
    return carregadas.filter((c) => pelaBusca.has(c) || (telefone && telefoneCasa(c.contato_telefone, busca)));
  }, [carregadas, search.active, search.results, busca]);

  return (
    <div className="space-y-4">
      {/* A barra da lista (docs/PLANO_BARRA_DE_LISTA.md): abas à esquerda, busca no resto. */}
      <BarraDaLista
        abas={
          <AbasDaLista
            nome="Fila"
            value={filaAtiva}
            onValueChange={setFila}
            abas={ABAS.map((a) => {
              const n = contagens.data?.[a.value];
              return {
                value: a.value,
                label: a.label,
                contagem: filaAtiva === a.value ? n : undefined,
                // O que espera o advogado fica à vista mesmo de outra aba.
                alerta: a.value === 'aguardando' && n ? { n } : undefined,
              };
            })}
          />
        }
        filtros={
          doEscritorio && gere ? (
            <FiltroDaLista
              eixo="Quem cuida"
              value={quemCuida}
              onValueChange={setQuemCuida}
              vazio={TODOS_OS_ADVOGADOS}
              rotuloVazio="Todos os advogados"
              opcoes={[
                ...(config?.participantes ?? []).map((p) => ({
                  value: p.usuario_id,
                  label: p.nome_tratamento || p.nome,
                })),
                {
                  value: SEM_ADVOGADO,
                  label: contagens.data?.sem_advogado
                    ? `Sem advogado (${contagens.data.sem_advogado})`
                    : 'Sem advogado',
                },
              ]}
            />
          ) : undefined
        }
        filtrosAtivos={filtraQuem !== TODOS_OS_ADVOGADOS ? 1 : 0}
        busca={
          <BuscaInteligente
            semNumero
            value={busca}
            onChange={setBusca}
            strictness={search.strictness}
            onStrictnessChange={search.setStrictness}
            resultCount={linhas.length}
            totalCount={carregadas.length}
            placeholder="Buscar por nome ou telefone…"
          />
        }
      />

      <Card>
        <CardContent className="p-3 sm:p-4">
          {lista.isLoading ? (
            <div className="space-y-2" role="status" aria-label="Carregando atendimentos">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[76px] rounded-lg" />
              ))}
            </div>
          ) : lista.error ? (
            <ErrorState
              title="Não foi possível carregar os atendimentos"
              description={classifyError(lista.error).message}
              actions={[{ label: 'Tentar novamente', onClick: () => lista.refetch() }]}
            />
          ) : linhas.length === 0 ? (
            <EmptyState
              icon={<Inbox />}
              title={busca ? 'Nenhum atendimento com esse nome ou telefone' : VAZIO[filaAtiva].title}
              description={busca ? 'Tente outro termo.' : VAZIO[filaAtiva].description}
            />
          ) : (
            <ul className="space-y-2">
              {linhas.map((c) => (
                <LinhaDoAtendimento key={c.id} conversa={c} mostrarAdvogado={doEscritorio && gere} />
              ))}
            </ul>
          )}

          {!busca && lista.hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={() => lista.fetchNextPage()}
                disabled={lista.isFetchingNextPage}
              >
                {lista.isFetchingNextPage && <Spinner className="mr-2 text-inherit" />}
                Carregar mais
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LinhaDoAtendimento({ conversa, mostrarAdvogado }: { conversa: ConversaResumo; mostrarAdvogado: boolean }) {
  const situacao = situacaoDe(conversa);
  const frase = fraseDoQuePrecisa(conversa);
  const precisa = conversa.precisa_de_mim;

  return (
    <li>
      <Link
        to={`/app/secretario-ia/atendimentos/${conversa.id}`}
        className={`relative block rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          precisa ? 'border-l-[3px] border-l-primary' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="break-words text-sm font-semibold">{nomeExibido(conversa)}</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              {conversa.area_nome && <span>{conversa.area_nome}</span>}
              {/* No escritório, quem gere vê de quem é cada um; sem advogado é a fila a distribuir. */}
              {mostrarAdvogado && (
                <span className={conversa.advogado_id ? undefined : 'font-medium text-amber-700 dark:text-amber-300'}>
                  {conversa.advogado_id ? conversa.advogado_nome : 'Sem advogado'}
                </span>
              )}
              <Badge variant="outline" className={`whitespace-nowrap ${classesDoTom(situacao.tom)}`}>
                {situacao.rotulo}
              </Badge>
            </div>
            <p className={`text-sm ${frase.tom === 'bad' ? 'font-medium text-destructive' : frase.tom === 'warn' ? 'font-medium text-amber-700 dark:text-amber-300' : frase.tom === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}`}>
              {frase.texto}
            </p>
          </div>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{quando(conversa.ultima_atividade_em)}</span>
        </div>
      </Link>
    </li>
  );
}
